import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { EMAIL_RULES_SYSTEM_ENUMS } from '@erp/shared-types';

import Page from 'src/components/page/Page';
import { useToast } from 'src/context/ToastContext';

import useCoreEnums from '@/api/core/core-enums/useCoreEnums';
import useEmailIngestConfig from '@/api/core/organization-config/useEmailIngestConfig';
import {
  useCreateEmailRule,
  useCreateEmailRuleEventAndLink,
  useDeleteEmailRule,
  useEmailRules,
  useUpdateEmailRule,
} from '@/api/rpc/hooks/communications/useEmailRules';

import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import type {
  EmailRule,
  EmailRuleEventOption,
  EmailRuleFilters,
  WorkflowName,
} from '@erp/rpc';
import {
  ArrowElbowDownRightIcon,
  DotsThreeVerticalIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  FlowArrowIcon,
} from '@phosphor-icons/react';

type FilterableField = 'from' | 'to' | 'cc' | 'subject' | 'mailbox';

const FILTERABLE_FIELDS: { key: FilterableField; label: string }[] = [
  { key: 'from', label: 'From' },
  { key: 'to', label: 'To' },
  { key: 'cc', label: 'CC' },
  { key: 'subject', label: 'Subject' },
  { key: 'mailbox', label: 'Mailbox' },
];

function dedupeStrings(values: string[]) {
  return [...new Set(values.map((v) => v.trim()).filter(Boolean))];
}

function normalizeFilters(filters: EmailRuleFilters): EmailRuleFilters {
  const normalized: EmailRuleFilters = {};

  FILTERABLE_FIELDS.forEach(({ key }) => {
    const values = dedupeStrings(filters[key] ?? []);
    if (values.length > 0) {
      normalized[key] = values;
    }
  });

  // Preserve hasAttachments if it's explicitly set (true or false)
  if (filters.hasAttachments !== undefined && filters.hasAttachments !== null) {
    normalized.hasAttachments = filters.hasAttachments;
  }

  return normalized;
}

type StagedTrigger = {
  name: string;
  description?: string;
};

type ExtendedEventOption = EmailRuleEventOption & {
  isStaged?: boolean;
};

function FiltersList({ filters }: { filters: EmailRuleFilters }) {
  const items = FILTERABLE_FIELDS.flatMap(({ key, label }) =>
    (filters[key] ?? []).map((value) => ({ label, value })),
  );

  const hasAttachmentsFilter =
    filters.hasAttachments !== undefined && filters.hasAttachments !== null;

  if (items.length === 0 && !hasAttachmentsFilter) {
    return <div className="text-sm text-muted-foreground">(no filters)</div>;
  }

  return (
    <ul className="list-disc pl-5 text-sm">
      {items.map(({ label, value }) => (
        <li key={`${label}:${value}`}>
          {' '}
          {label} contains "{value}"
        </li>
      ))}
      {hasAttachmentsFilter && (
        <li key="hasAttachments">
          {filters.hasAttachments ? 'Has attachments' : 'No attachments'}
        </li>
      )}
    </ul>
  );
}

function TriggersList({ events }: { events: EmailRuleEventOption[] }) {
  if (events.length === 0) {
    return <div className="text-sm text-muted-foreground">(none)</div>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {events.map((e) => (
        <Badge key={e.id} variant="secondary">
          {e.name}
        </Badge>
      ))}
    </div>
  );
}

function WorkflowsList({ workflows }: { workflows?: WorkflowName[] }) {
  if (!workflows || workflows.length === 0) {
    return <div className="text-sm text-muted-foreground">(none)</div>;
  }

  return (
    <ul className="list-disc pl-5 text-sm space-y-1">
      {workflows.map((wf) => (
        <li key={wf.id}>
          <Link
            to="/workflows/$workflowDefinitionId"
            params={{ workflowDefinitionId: wf.id }}
            className="text-primary hover:underline"
          >
            {wf.name}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function RuleViewCard({
  rule,
  onEdit,
  onDelete,
  onCreateWorkflow,
  isNewlyCreated,
}: {
  rule: EmailRule;
  onEdit: () => void;
  onDelete: () => void;
  onCreateWorkflow?: (eventOptionId: string) => void;
  isNewlyCreated?: boolean;
}) {
  const hasNoWorkflows = !rule.workflows || rule.workflows.length === 0;
  const showCreateWorkflowPrompt = isNewlyCreated && hasNoWorkflows;

  return (
    <Card className={isNewlyCreated ? 'ring-2 ring-primary/50' : undefined}>
      <CardHeader className="gap-2">
        <CardTitle className="flex items-center gap-2">
          {rule.ruleName}
          {isNewlyCreated && (
            <Badge variant="default" className="text-xs">
              New
            </Badge>
          )}
        </CardTitle>
        <CardAction className="flex items-center gap-2">
          <Badge variant={rule.enabled ? 'secondary' : 'outline'}>
            {rule.enabled ? 'Enabled' : 'Disabled'}
          </Badge>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Rule actions">
                <DotsThreeVerticalIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <PencilIcon /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onClick={onDelete}>
                <TrashIcon /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardAction>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="text-sm font-medium">When the email matches:</div>
          <FiltersList filters={rule.filters ?? {}} />
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Trigger:</div>
          <TriggersList events={rule.events ?? []} />
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Linked Workflows:</div>
          <WorkflowsList workflows={rule.workflows} />
        </div>

        {showCreateWorkflowPrompt &&
          rule.events.length > 0 &&
          onCreateWorkflow && (
            <div className="pt-2 border-t">
              <p className="text-sm text-muted-foreground mb-2">
                Create a workflow to run when this rule triggers:
              </p>
              <div className="flex flex-wrap gap-2">
                {rule.events.map((event) => (
                  <Button
                    key={event.id}
                    variant="outline"
                    size="sm"
                    onClick={() => onCreateWorkflow(event.id)}
                  >
                    <FlowArrowIcon className="h-4 w-4 mr-2" />
                    Create workflow for "{event.name}"
                  </Button>
                ))}
              </div>
            </div>
          )}
      </CardContent>
    </Card>
  );
}

function RuleEditorCard({
  rule,
  eventOptions,
  mailboxSuggestions,
  onCreated,
  onCancel,
  onEventsChanged,
}: {
  rule?: EmailRule;
  eventOptions: EmailRuleEventOption[];
  mailboxSuggestions?: string[];
  onCreated?: (createdRuleId?: string) => void;
  onCancel?: () => void;
  onEventsChanged?: () => void;
}) {
  const { showToast } = useToast();

  const isEdit = Boolean(rule);

  const [ruleName, setRuleName] = useState(rule?.ruleName ?? '');
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [filters, setFilters] = useState<EmailRuleFilters>(rule?.filters ?? {});

  const [selectedEventIds, setSelectedEventIds] = useState<string[]>(
    rule?.events.map((e) => e.id) ?? [],
  );
  const [triggerSearchValue, setTriggerSearchValue] = useState('');

  const [filterField, setFilterField] = useState<FilterableField>('to');
  const [filterDraft, setFilterDraft] = useState('');

  const [stagedTriggers, setStagedTriggers] = useState<StagedTrigger[]>([]);

  const { mutateAsync: createRule, isPending: isCreating } =
    useCreateEmailRule();
  const { mutateAsync: updateRule, isPending: isUpdating } =
    useUpdateEmailRule();
  const { mutateAsync: createEventAndLink, isPending: isCreatingEvent } =
    useCreateEmailRuleEventAndLink();

  const addFilter = () => {
    const value = filterDraft.trim();
    if (!value) return;

    setFilters((prev) => {
      const existing = prev[filterField] ?? [];
      return {
        ...prev,
        [filterField]: dedupeStrings([...existing, value]),
      };
    });

    setFilterDraft('');
  };

  const removeFilter = (field: FilterableField, value: string) => {
    setFilters((prev) => {
      const existing = prev[field] ?? [];
      const next = existing.filter((v) => v !== value);
      const updated: EmailRuleFilters = {
        ...prev,
        [field]: next,
      };

      if (next.length === 0) {
        delete (updated as Partial<EmailRuleFilters>)[field];
      }

      return updated;
    });
  };

  const triggerNameToAdd = triggerSearchValue.trim();

  // Merge eventOptions with staged triggers for display in autocomplete
  const mergedEventOptions: ExtendedEventOption[] = [
    ...eventOptions,
    ...stagedTriggers.map((t, idx) => ({
      id: `staged-${idx}-${t.name}`,
      name: t.name,
      description: t.description ?? null,
      sortOrder: 9999 + idx, // Place staged triggers at the end
      isStaged: true,
    })),
  ];

  const hasExactMatch = triggerNameToAdd
    ? mergedEventOptions.some(
        (o) => (o.name ?? '').toLowerCase() === triggerNameToAdd.toLowerCase(),
      )
    : false;

  const showCreateOption = Boolean(triggerNameToAdd) && !hasExactMatch;

  // Add synthetic "create new" option when applicable
  const CREATE_OPTION_ID = '__create_new__';
  const optionsWithCreate: ExtendedEventOption[] = showCreateOption
    ? [
        ...mergedEventOptions,
        {
          id: CREATE_OPTION_ID,
          name: `+ Create "${triggerNameToAdd}"`,
          description: null,
          sortOrder: 99999,
          isStaged: false,
        },
      ]
    : mergedEventOptions;

  const canSubmit = ruleName.trim().length > 0;

  const handleSave = async () => {
    try {
      if (!canSubmit) {
        showToast({
          title: 'Rule Name Required',
          message: 'Please provide a rule name before saving.',
          color: 'warning',
          icon: 'warning',
        });
        return;
      }

      // Filter out staged trigger IDs - they're not real UUIDs
      const realEventOptionIds = selectedEventIds.filter(
        (id) => !id.startsWith('staged-'),
      );

      const payload = {
        ruleName: ruleName.trim(),
        enabled,
        filters: normalizeFilters(filters),
        eventOptionIds: realEventOptionIds,
      };

      if (isEdit) {
        if (!rule) return;
        await updateRule({
          id: rule.id,
          ...payload,
        });
        showToast({
          title: '✅ Rule Updated',
          message: `Saved "${payload.ruleName}"`,
          color: 'success',
          icon: 'check_circle',
        });
        onCreated?.();
        return;
      }

      const created = await createRule(payload);

      if (stagedTriggers.length > 0) {
        const createdRuleId = created.rule.id;

        await Promise.all(
          stagedTriggers.map((t) =>
            createEventAndLink({
              ruleId: createdRuleId,
              name: t.name,
              description: t.description,
            }),
          ),
        );

        onEventsChanged?.();
      }

      showToast({
        title: '✅ Rule Created',
        message: `Created "${payload.ruleName}"`,
        color: 'success',
        icon: 'check_circle',
      });

      setRuleName('');
      setEnabled(true);
      setFilters({});
      setSelectedEventIds([]);
      setTriggerSearchValue('');
      setStagedTriggers([]);

      onCreated?.(created.rule.id);
    } catch (error) {
      showToast({
        title: '💥 Error Saving Rule',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'error',
        icon: 'error',
      });
    }
  };

  const handleAddNewTrigger = async () => {
    if (!showCreateOption) return;

    const name = triggerNameToAdd;

    if (!rule) {
      // Staging mode: add to local state and auto-select
      const stagedId = `staged-${stagedTriggers.length}-${name}`;
      setStagedTriggers((prev) => [...prev, { name, description: undefined }]);
      setSelectedEventIds((prev) => [...prev, stagedId]);
      setTriggerSearchValue('');

      showToast({
        title: 'Trigger staged',
        message: `"${name}" will be created when you save this rule.`,
        color: 'info',
        icon: 'info',
      });
      return;
    }

    // Edit mode: create immediately via API
    try {
      const response = await createEventAndLink({
        ruleId: rule.id,
        name,
        description: undefined,
      });

      const responseIds = response.rule.events.map((e) => e.id);
      setSelectedEventIds((prev) => [...new Set([...prev, ...responseIds])]);

      onEventsChanged?.();
      setTriggerSearchValue('');

      showToast({
        title: 'Trigger created',
        message: `Created "${name}"`,
        color: 'success',
        icon: 'check_circle',
      });
    } catch (error) {
      showToast({
        title: 'Error creating trigger',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'error',
        icon: 'error',
      });
    }
  };

  const pending = isCreating || isUpdating;

  return (
    <Card>
      <CardHeader className="gap-2">
        <CardTitle className="flex items-center gap-3">
          <span>{isEdit ? 'Email Rule' : 'New Email Rule'}</span>
        </CardTitle>
        <CardAction className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {enabled ? 'Enabled' : 'Disabled'}
            </span>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>
        </CardAction>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-xs">Rule name</Label>
          <Input
            value={ruleName}
            onChange={(e) => setRuleName(e.target.value)}
            placeholder="e.g. Invoice Intake"
          />
        </div>

        <div className="space-y-3">
          <div className="text-sm font-medium">When the email matches:</div>
          <div className="flex flex-wrap gap-2">
            {FILTERABLE_FIELDS.flatMap(({ key, label }) =>
              (filters[key] ?? []).map((value) => (
                <Badge
                  key={`${key}:${value}`}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  {label}: {value}
                  <button
                    type="button"
                    className="ml-1 rounded-full p-0.5 hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => removeFilter(key, value)}
                  >
                    ×
                  </button>
                </Badge>
              )),
            )}
            {filters.hasAttachments !== undefined &&
              filters.hasAttachments !== null && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  {filters.hasAttachments
                    ? 'Has attachments'
                    : 'No attachments'}
                  <button
                    type="button"
                    className="ml-1 rounded-full p-0.5 hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => {
                      setFilters((prev) => {
                        const { hasAttachments: _, ...rest } = prev;
                        return rest;
                      });
                    }}
                  >
                    ×
                  </button>
                </Badge>
              )}
            {FILTERABLE_FIELDS.every(
              ({ key }) => (filters[key] ?? []).length === 0,
            ) &&
              (filters.hasAttachments === undefined ||
                filters.hasAttachments === null) && (
                <span className="text-xs text-muted-foreground">
                  (no filters)
                </span>
              )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={filterField}
              onValueChange={(v) => {
                if (FILTERABLE_FIELDS.some(({ key }) => key === v)) {
                  setFilterField(v as FilterableField);
                }
              }}
            >
              <SelectTrigger size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTERABLE_FIELDS.map(({ key, label }) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="text-xs">contains</div>

            <div className="flex-1 min-w-[220px]">
              {filterField === 'mailbox' && mailboxSuggestions?.length ? (
                <Autocomplete
                  options={mailboxSuggestions}
                  multiSelect={false}
                  value={filterDraft || undefined}
                  onValueChange={(value) => {
                    if (value) {
                      // Auto-add when selecting from dropdown
                      setFilters((prev) => ({
                        ...prev,
                        mailbox: dedupeStrings([
                          ...(prev.mailbox ?? []),
                          value,
                        ]),
                      }));
                      setFilterDraft('');
                    }
                  }}
                  searchValue={filterDraft}
                  onSearchValueChange={setFilterDraft}
                  getValue={(opt) => opt}
                  getLabel={(opt) => opt}
                  placeholder="Select or type mailbox..."
                  searchPlaceholder="Search mailboxes..."
                  emptyMessage="No mailboxes found. Type to filter."
                />
              ) : (
                <Input
                  value={filterDraft}
                  onChange={(e) => setFilterDraft(e.target.value)}
                  placeholder={
                    filterField === 'subject'
                      ? 'Subject contains text...'
                      : filterField === 'mailbox'
                        ? 'mailbox@example.com'
                        : 'invoices@gently-ai.com'
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addFilter();
                    }
                  }}
                />
              )}
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!filterDraft.trim()}
              onClick={addFilter}
            >
              <PlusIcon />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <div className="text-xs">Attachments:</div>
            <Select
              value={
                filters.hasAttachments === true
                  ? 'yes'
                  : filters.hasAttachments === false
                    ? 'no'
                    : 'any'
              }
              onValueChange={(v) => {
                setFilters((prev) => {
                  if (v === 'yes') {
                    return { ...prev, hasAttachments: true };
                  } else if (v === 'no') {
                    return { ...prev, hasAttachments: false };
                  } else {
                    // Remove hasAttachments from filters
                    const { hasAttachments: _, ...rest } = prev;
                    return rest;
                  }
                });
              }}
            >
              <SelectTrigger size="sm" className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="yes">Has attachments</SelectItem>
                <SelectItem value="no">No attachments</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-row items-start gap-2 pl-2">
          <ArrowElbowDownRightIcon className="w-4 h-4" />

          <div className="text-sm font-medium">Trigger:</div>

          <div className="flex flex-col gap-2">
            <Autocomplete
              options={optionsWithCreate}
              multiSelect
              value={selectedEventIds}
              onValueChange={(ids) => {
                // Handle "create new" option selection
                if (ids.includes(CREATE_OPTION_ID)) {
                  handleAddNewTrigger();
                  return;
                }
                setSelectedEventIds(ids);
              }}
              searchValue={triggerSearchValue}
              onSearchValueChange={setTriggerSearchValue}
              getValue={(opt) => opt.id}
              getLabel={(opt) => opt.name ?? ''}
              placeholder="Trigger these events..."
              searchPlaceholder="Search or create events..."
              emptyMessage="Type to create a new event"
              renderOption={(opt, isSelected) => {
                // Special rendering for "create new" option
                if (opt.id === CREATE_OPTION_ID) {
                  return (
                    <div className="flex items-center gap-2 text-primary font-medium">
                      <PlusIcon className="h-4 w-4" />
                      <span>Create "{triggerNameToAdd}"</span>
                    </div>
                  );
                }

                const isStaged = (opt as ExtendedEventOption).isStaged;

                return (
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${isStaged ? 'italic' : ''}`}>
                        {opt.name ?? ''}
                      </span>
                      {isStaged && (
                        <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          pending
                        </span>
                      )}
                      {isSelected && !isStaged && (
                        <span className="text-xs text-muted-foreground">
                          selected
                        </span>
                      )}
                    </div>
                    {opt.description ? (
                      <span className="text-xs text-muted-foreground">
                        {opt.description}
                      </span>
                    ) : null}
                  </div>
                );
              }}
            />

            {/* Editable staged triggers */}
            {stagedTriggers.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground">
                  Pending triggers (created when you save):
                </div>
                {stagedTriggers.map((trigger, idx) => {
                  const stagedId = `staged-${idx}-${trigger.name}`;
                  return (
                    <div
                      key={stagedId}
                      className="flex items-start gap-2 rounded-md border p-2"
                    >
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">
                            {trigger.name}
                          </span>
                          <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                            pending
                          </span>
                        </div>
                        <Input
                          value={trigger.description ?? ''}
                          onChange={(e) => {
                            setStagedTriggers((prev) =>
                              prev.map((t, i) =>
                                i === idx
                                  ? {
                                      ...t,
                                      description: e.target.value || undefined,
                                    }
                                  : t,
                              ),
                            );
                          }}
                          placeholder="Optional description..."
                          className="h-7 text-xs"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => {
                          setStagedTriggers((prev) =>
                            prev.filter((_, i) => i !== idx),
                          );
                          setSelectedEventIds((prev) =>
                            prev.filter((id) => id !== stagedId),
                          );
                        }}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </CardContent>

      <div className="px-3 pb-3 flex justify-end gap-2">
        {isEdit && onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
        )}
        <Button onClick={handleSave} disabled={!canSubmit || pending}>
          {pending ? 'Saving…' : isEdit ? 'Save Rule' : 'Create Rule'}
        </Button>
      </div>
    </Card>
  );
}

const InboundEmailRulesPage: React.FC = () => {
  const navigate = useNavigate();
  const rulesQuery = useEmailRules();
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createDraftKey, setCreateDraftKey] = useState(0);
  const [newlyCreatedRuleId, setNewlyCreatedRuleId] = useState<string | null>(
    null,
  );

  const { showToast } = useToast();
  const { mutateAsync: deleteRule, isPending: isDeleting } =
    useDeleteEmailRule();

  const handleCreateWorkflow = (emailRuleOptionId: string) => {
    navigate({
      to: '/workflows/$workflowDefinitionId',
      params: { workflowDefinitionId: 'new' },
      search: { edit: true, emailRuleOptionId },
    });
  };

  // Fetch the Email Rule Events enum options.
  // We delay until after rules load so the backend has ensured the system enum exists.
  const coreEnumsQuery = useCoreEnums(
    {
      name: EMAIL_RULES_SYSTEM_ENUMS.EMAIL_RULE_EVENTS.name,
      includeOptions: true,
    },
    {
      enabled: Boolean(rulesQuery.data),
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
    },
  );

  // Fetch configured mailbox UPNs for autocomplete suggestions
  const emailIngestConfigQuery = useEmailIngestConfig();
  const mailboxSuggestions =
    emailIngestConfigQuery.data?.emailIngestConfig?.mailboxUpns ?? [];

  const eventOptions =
    (coreEnumsQuery.data?.edges?.[0]?.options as EmailRuleEventOption[]) ?? [];

  const rules = rulesQuery.data?.rules ?? [];

  return (
    <Page
      title="Email Rules"
      subtitle="Define triggers when inbound emails match headers"
      isLoading={rulesQuery.isLoading}
      error={(rulesQuery.error as Error) ?? undefined}
      actions={
        <Button
          variant={isCreating ? 'outline' : 'default'}
          onClick={() => {
            if (isCreating) {
              setIsCreating(false);
              setCreateDraftKey((k) => k + 1);
            } else {
              setIsCreating(true);
            }
          }}
        >
          {isCreating ? 'Cancel' : '+ Create'}
        </Button>
      }
    >
      <div className="space-y-6 pt-3">
        {isCreating && (
          <RuleEditorCard
            key={`create-${createDraftKey}`}
            eventOptions={eventOptions}
            mailboxSuggestions={mailboxSuggestions}
            onEventsChanged={() => coreEnumsQuery.refetch()}
            onCreated={(createdRuleId) => {
              setIsCreating(false);
              setCreateDraftKey((k) => k + 1);
              if (createdRuleId) {
                setNewlyCreatedRuleId(createdRuleId);
              }
              rulesQuery.refetch();
              coreEnumsQuery.refetch();
            }}
            onCancel={() => {
              setIsCreating(false);
              setCreateDraftKey((k) => k + 1);
            }}
          />
        )}

        <div className="space-y-4">
          {rules.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No rules yet. Create one above.
            </div>
          ) : (
            rules.map((r) => {
              if (editingRuleId === r.id) {
                return (
                  <RuleEditorCard
                    key={`${r.id}-${r.updatedAt.getTime()}`}
                    rule={r}
                    eventOptions={eventOptions}
                    mailboxSuggestions={mailboxSuggestions}
                    onEventsChanged={() => coreEnumsQuery.refetch()}
                    onCreated={() => {
                      setEditingRuleId(null);
                      rulesQuery.refetch();
                      coreEnumsQuery.refetch();
                    }}
                    onCancel={() => setEditingRuleId(null)}
                  />
                );
              }

              return (
                <RuleViewCard
                  key={r.id}
                  rule={r}
                  onEdit={() => {
                    setEditingRuleId(r.id);
                    setNewlyCreatedRuleId(null);
                  }}
                  onDelete={async () => {
                    if (isDeleting) return;

                    const ok = window.confirm(
                      `Delete rule "${r.ruleName}"? This will not delete trigger enums/options.`,
                    );
                    if (!ok) return;

                    try {
                      await deleteRule({ id: r.id });
                      showToast({
                        title: '✅ Rule Deleted',
                        message: `Deleted "${r.ruleName}"`,
                        color: 'success',
                        icon: 'check_circle',
                      });
                      if (editingRuleId === r.id) setEditingRuleId(null);
                      if (newlyCreatedRuleId === r.id)
                        setNewlyCreatedRuleId(null);
                      rulesQuery.refetch();
                    } catch (error) {
                      showToast({
                        title: '💥 Error Deleting Rule',
                        message: (error as Error).message,
                        color: 'error',
                        icon: 'error',
                      });
                    }
                  }}
                  isNewlyCreated={newlyCreatedRuleId === r.id}
                  onCreateWorkflow={handleCreateWorkflow}
                />
              );
            })
          )}
        </div>
      </div>
    </Page>
  );
};

export { InboundEmailRulesPage };
